import type { ReactElement } from 'react'
import { AppShell } from '../components/AppShell.js'
import { Button } from '../components/ui/button.js'
import { Icon } from '../components/installer/icons.js'
import { PROOF_STEPS } from '../lib/proofSteps.js'
import { capturedCount, isCaptured, useDemoStore } from '../state/store.js'
import { useStep } from '../state/useStep.js'

export const ProofBundleScreen = (): ReactElement => {
  const { state, dispatch } = useDemoStore()
  const { goto } = useStep()

  const count = capturedCount(state)
  const total = PROOF_STEPS.length
  const diff = total - count

  const footer = (
    <Button
      size="xl"
      disabled={count < total}
      onClick={() => {
        if (count === total) goto('summary')
      }}
    >
      {count === total ? (
        <>
          <Icon.CheckCircle /> Continue to Summary
        </>
      ) : count === 0 ? (
        'Tap a photo to begin'
      ) : (
        `${diff} photo${diff === 1 ? '' : 's'} remaining`
      )}
    </Button>
  )

  return (
    <AppShell
      title="Proof bundle"
      showBack
      onBack={() => goto('job-brief')}
      progress={(30 + (count / total) * 60) / 100}
      footer={footer}
    >
      <div className="installer-surface page fade-in">
        {/* Info card */}
        <div className="card info">
          <div className="intake-header">
            <div className="intake-icon">
              <Icon.Camera />
            </div>
            <div style={{ flex: 1 }}>
              <div className="intake-title">5-PHOTO CLOSE-OUT SEQUENCE</div>
              <div className="intake-desc">
                Capture each photo in order. Veritact analyzes each one live and flags any
                defect before you leave the home.
              </div>
            </div>
          </div>
          <div className="risk-row" style={{ marginBottom: 0 }}>
            <span className="chip time">
              <Icon.Clock />
              <span>
                {count} of {total} captured
              </span>
            </span>
          </div>
        </div>

        {/* Required photos list */}
        <div className="section-label">REQUIRED PHOTOS</div>
        <div>
          {PROOF_STEPS.map((photo, idx) => {
            const captured = isCaptured(state, photo.id)
            return (
              <div
                key={photo.id}
                className={`bundle-item ${captured ? 'captured' : ''}`}
                onClick={() => {
                  if (!captured) {
                    dispatch({ type: 'START_PHOTO', idx })
                    goto('capture')
                  }
                }}
              >
                <div className="bundle-check">{captured && <Icon.Check />}</div>
                <div className="bundle-item-body">
                  <div className="bundle-item-label">
                    <span className="bundle-item-num">{photo.num}</span> {photo.label}
                  </div>
                  <div className="bundle-item-status">
                    {captured ? 'Verified' : 'Tap to capture'}
                  </div>
                </div>
                <div className="bundle-item-chevron">
                  {!captured && (
                    <span style={{ color: 'var(--text-tertiary)' }}>
                      <Icon.Chevron />
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Footnote */}
        <div className="card neutral" style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            All five photos must pass verification before the job can close. If a photo flags
            yellow or red, follow the on-screen correction guidance and recapture.
          </div>
        </div>
      </div>
    </AppShell>
  )
}
